import type { PersonEfteruddannelse } from "@/interfaces/schema";
import { roundLessons } from "@/util/number";

import { getTransferredIndberetninger } from "./getTransferredIndberetninger";
import { getTransferredOutLessonsByType } from "./transferredLessons";

export interface PeriodLessonTypeRow {
  typeNr: number;
  typeLabel: string;
  reported: number;
  transferredIn: number;
  transferredOut: number;
}

const getTypeLabel = (typeNr: number, navn?: string | null): string =>
  navn?.trim() || String(typeNr);

export const buildPeriodLessonTypeRows = (
  period: PersonEfteruddannelse,
  allTypes?: KodeListe[],
  allPeriods: PersonEfteruddannelse[] = [],
): PeriodLessonTypeRow[] => {
  const periodeudloeb = period.personEfteruddannelseIndberetning.find(
    (indberetning) =>
      indberetning.efteruddannelseIndberetningstype === "PERIODEUDLOEB",
  );
  const transferredIndberetninger = getTransferredIndberetninger(period);

  const rowsByType = new Map<number, PeriodLessonTypeRow>();

  const getRow = (
    typeNr: number,
    navn?: string | null,
  ): PeriodLessonTypeRow => {
    const existing = rowsByType.get(typeNr);
    if (existing) return existing;

    const row: PeriodLessonTypeRow = {
      typeNr,
      typeLabel: getTypeLabel(typeNr, navn),
      reported: 0,
      transferredIn: 0,
      transferredOut: 0,
    };
    rowsByType.set(typeNr, row);

    return row;
  };

  allTypes?.forEach((type) => getRow(type.nr, type.navn));

  periodeudloeb?.personEfteruddannelseIndberetningLinje.forEach((linje) => {
    const row = getRow(
      linje.efteruddannelsestypenr,
      linje.efteruddannelsestypenrNavigation?.navn,
    );
    row.reported = roundLessons(row.reported + linje.indberettedeLektioner);
  });

  transferredIndberetninger.forEach((indberetning) => {
    indberetning.personEfteruddannelseIndberetningLinje.forEach((linje) => {
      const row = getRow(
        linje.efteruddannelsestypenr,
        linje.efteruddannelsestypenrNavigation?.navn,
      );
      row.transferredIn = roundLessons(
        row.transferredIn + linje.indberettedeLektioner,
      );
    });
  });

  getTransferredOutLessonsByType(period, allPeriods).forEach(
    (lessons, typeNr) => {
      getRow(typeNr).transferredOut = lessons;
    },
  );

  return [...rowsByType.values()].sort((a, b) => a.typeNr - b.typeNr);
};

export const sumPeriodLessonTypeColumn = (
  rows: PeriodLessonTypeRow[],
  column: "reported" | "transferredIn" | "transferredOut",
): number => roundLessons(rows.reduce((total, row) => total + row[column], 0));
